import { useEffect, useState } from 'react'

/**
 * Full-screen confetti celebration burst.
 * Particles explode from center, spread across entire viewport,
 * then fade out. Regenerated on each trigger for variety.
 *
 * @param {number} seed — change to trigger a new burst
 */

const COLORS = [
  '#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
  '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE',
  '#85C1E9', '#F8C471', '#82E0AA', '#F1948A', '#AED6F1',
]

const EMOJIS = ['✨', '🎉', '⭐', '🌟', '🎊', '💫', '🏆', '🎈', '🥳', '💥']

function rand(min, max) {
  return min + Math.random() * (max - min)
}

function createParticles() {
  // Scale to viewport so particles reach edges on any screen
  const vw = typeof window !== 'undefined' ? window.innerWidth : 800
  const vh = typeof window !== 'undefined' ? window.innerHeight : 600
  const maxReach = Math.max(vw, vh) * 0.7
  const count = vw > 1200 ? 80 : vw > 768 ? 60 : 45

  return Array.from({ length: count }, (_, i) => {
    const angle = (Math.PI * 2 * i) / count + rand(-0.4, 0.4)
    const velocity = rand(maxReach * 0.3, maxReach)
    const isEmoji = i < Math.floor(count * 0.2)
    return {
      id: i,
      isEmoji,
      emoji: isEmoji ? EMOJIS[i % EMOJIS.length] : null,
      color: COLORS[i % COLORS.length],
      x: Math.cos(angle) * velocity,
      y: Math.sin(angle) * velocity - rand(vh * 0.15, vh * 0.5),
      rotation: rand(0, 900),
      scale: isEmoji ? rand(1.0, 2.0) : rand(0.5, 1.2),
      delay: rand(0, 200),
      duration: rand(900, 1600),
      size: isEmoji ? 0 : rand(6, 16),
      shape: i % 3,
    }
  })
}

export default function ConfettiBurst({ seed }) {
  const [state, setState] = useState(null) // { particles, key }

  useEffect(() => {
    if (!seed) return
    setState({ particles: createParticles(), key: seed })
    const timer = setTimeout(() => setState(null), 2200)
    return () => clearTimeout(timer)
  }, [seed])

  if (!state) return null

  return (
    <div
      key={state.key}
      className="pointer-events-none fixed inset-0 z-[100] overflow-hidden"
      aria-hidden="true"
    >
      {/* Flash overlay */}
      <div className="absolute inset-0 animate-[flash_0.6s_ease-out_forwards] bg-white/30 dark:bg-white/15" />

      {/* Particles from center */}
      {state.particles.map((p) => (
        <div
          key={p.id}
          className="absolute left-1/2 top-1/2"
          style={{
            animation: `confetti-burst ${p.duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${p.delay}ms forwards`,
            '--tx': `${p.x}px`,
            '--ty': `${p.y}px`,
            '--rot': `${p.rotation}deg`,
            '--scale': p.scale,
          }}
        >
          {p.isEmoji ? (
            <span style={{ fontSize: `${p.scale * 1.4}rem` }}>{p.emoji}</span>
          ) : (
            <div
              style={{
                width: p.shape === 2 ? p.size * 2.5 : p.size,
                height: p.shape === 0 ? p.size : p.size * 0.5,
                backgroundColor: p.color,
                borderRadius: p.shape === 0 ? '50%' : '2px',
              }}
            />
          )}
        </div>
      ))}
    </div>
  )
}
